"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingUp, ShoppingBag, Building2, RefreshCw, Compass, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const audienceSegments = [{
  id: 1,
  badge: "Scale & Growth",
  title: "Growing UK SMEs",
  description: "You are ready to scale organic search revenue but not ready for the financial overhead of a full-time SEO director. My fractional head of SEO service gives you senior leadership at a fraction of the cost.",
  highlight: "Direct strategic roadmap without full-time executive payroll",
  iconName: "TrendingUp",
  hasEcommerceLink: false
}, {
  id: 2,
  badge: "Online Retail",
  title: "E-commerce stores",
  description: "You run an online store and need an experienced SEO specialist to drive product page optimization, category taxonomy fixes, schema markup, and content structured around buyer intent.",
  highlight: "High-intent keyword focus to drive direct transaction growth",
  iconName: "ShoppingBag",
  hasEcommerceLink: true
}, {
  id: 3,
  badge: "Lead Generation",
  title: "Service-based businesses",
  description: "You want high-value inbound enquiries from prospective clients. I optimize your Google Business Profile, high-converting service landing pages, and customer review signals for maximum local and national return.",
  highlight: "Targeted landing pages built to convert searchers into inquiries",
  iconName: "Building2",
  hasEcommerceLink: false
}, {
  id: 4,
  badge: "Agency Recovery",
  title: "Businesses tired of agency turnover",
  description: "You need senior strategic oversight and accountability rather than junior agency churn. As your fractional head of SEO, I embed high-level leadership into your business, mentor internal teams, and align search directly with revenue.",
  highlight: "Direct communication, complete transparency, and revenue-led metrics",
  iconName: "RefreshCw",
  hasEcommerceLink: false
}, {
  id: 5,
  badge: "Major Transitions",
  title: "Companies undergoing change",
  description: "You are planning a site migration, rebrand, expansion into new regional markets, or launching new offerings. I provide targeted technical oversight to protect your equity and capture new demand.",
  highlight: "Risk-free migration protection and new territory launch blueprints",
  iconName: "Compass",
  hasEcommerceLink: false
}];
export default function TargetAudience() {
  const shouldReduceMotion = useReducedMotion();
  const getSegmentIcon = (iconName: string) => {
    switch (iconName) {
      case "TrendingUp":
        return <TrendingUp className="size-5 text-primary" />;
      case "ShoppingBag":
        return <ShoppingBag className="size-5 text-primary" />;
      case "Building2":
        return <Building2 className="size-5 text-primary" />;
      case "RefreshCw":
        return <RefreshCw className="size-5 text-primary" />;
      case "Compass":
        return <Compass className="size-5 text-primary" />;
      default:
        return <TrendingUp className="size-5 text-primary" />;
    }
  };
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="targetaudiencefractionalseo" data-nav="light" className="relative bg-background py-20 lg:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16">
          <Badge variant="secondary" className="mb-4 px-3.5 py-1 text-xs font-semibold text-primary bg-secondary uppercase tracking-wider rounded-full">
            Ideal Client Fit
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
            Who my fractional SEO service is for
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            This collaborative model is built for small to medium-sized businesses ready to turn organic search into a predictable growth channel without hiring an expensive in-house executive.
          </p>
        </div>

        {/* Audience Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {audienceSegments.map((segment, i) => <motion.div key={segment.id} data-index={i} variants={cardVariants} className={`flex flex-col ${i === 4 ? "md:col-span-2 lg:col-span-1" : ""}`}>
              <Card className="h-full border border-border bg-card/60 hover:border-primary/40 hover:shadow-md transition-[color,background-color,border-color,box-shadow] duration-300 rounded-xl flex flex-col justify-between">
                <CardHeader className="p-6 pb-4">
                  <div className="flex items-center justify-between gap-2 mb-4">
                    <div className="p-2.5 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                      {getSegmentIcon(segment.iconName)}
                    </div>
                    <Badge variant="outline" className="text-xs font-medium text-muted-foreground border-border bg-background">
                      {segment.badge}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl font-semibold text-foreground tracking-tight">
                    {segment.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="p-6 pt-0 flex-1 flex flex-col justify-between">
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {segment.hasEcommerceLink ? <>
                          You run an online store and need an experienced SEO specialist to lead product page optimization, category taxonomy fixes, schema markup, and content strategy targeting buyer intent keywords. Explore my dedicated{" "}
                          <Link to="/EcommerceSeoPage" className="font-medium text-primary hover:underline underline-offset-2 transition-colors">
                            E-commerce SEO
                          </Link>{" "}
                          services.
                        </> : segment.description}
                    </p>

                    <div className="pt-3 border-t border-border/80 flex items-start gap-2.5">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                      <span className="text-xs font-medium text-foreground leading-snug">
                        {segment.highlight}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}

          {/* Direct CTA summary box */}
          <motion.div data-index={5} variants={cardVariants} className="md:col-span-2 lg:col-span-1 flex flex-col">
            <div className="h-full rounded-xl p-6 bg-primary text-primary-foreground flex flex-col justify-between shadow-md border border-primary/20">
              <div className="space-y-3">
                <Badge className="bg-white/20 text-white border-transparent text-xs uppercase tracking-wider rounded-full">
                  Embedded Leadership
                </Badge>
                <h3 className="text-xl font-semibold tracking-tight text-white">
                  Embedded fractional leadership
                </h3>
                <p className="text-sm text-primary-foreground/90 leading-relaxed">
                  Embed experienced executive-level search leadership into your business. I lead your organic growth strategy, guide internal teams, and deliver senior-level accountability on flexible fractional terms.
                </p>
              </div>

              <div className="pt-6 mt-4 border-t border-white/20">
                <Link to="/ContactPage" className="inline-block w-full">
                  <Button variant="secondary" className="w-full bg-white text-primary hover:bg-white/90 font-medium shadow-sm flex items-center justify-center gap-2 group cursor-pointer">
                    <span>Discuss your project</span>
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Closing Note Banner */}
        <motion.div initial={{
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="mt-14 rounded-2xl bg-muted/70 border border-border p-6 sm:p-8 text-center max-w-4xl mx-auto">
          <p className="text-sm sm:text-base font-medium text-foreground leading-relaxed">
            Fractional SEO fills the gap before hiring a full-time SEO director. If you have internal resources or developers but lack senior strategic direction connecting SEO to actual business growth, you are the right fit.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-4">
            <Link to="/ContactPage">
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-6">
                Book a Free Consultation
              </Button>
            </Link>
            <Link to="/CaseStudiesPage">
              <Button variant="outline" className="border-border text-foreground hover:bg-background/80">
                View Case Studies
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>;
}